import {
  ProviderInstance,
  ProviderInstanceShortLabel,
  StatusGetInstances,
} from '@/service/one-click-deploy/type';
import BadgeStatus from '../../components/BadgeStatus';
import { Label } from '@/components/ui/label';
import {
  ClockIcon,
  ExclamationTriangleIcon,
  ExternalLinkIcon,
} from '@radix-ui/react-icons';
import dayjs from 'dayjs';
import { fNumber } from '@/utils/formatNumber';
import { Separator } from '@/components/ui/separator';
import CopyIconButton from '@/components/ui/copy-to-clipboard/copy-icon-button';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import ConfirmationDialog from '@/components/ui/dialog-wrapper/ConfirmationDialog';
import { cn } from '@/lib/utils';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Box, Cpu, ServerOffIcon, Terminal } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import PlaygroundTextGeneration from '@/features/playground/text-generation';
import {
  useGetDedicateInstance,
  useRegenerateAPI,
} from '@/service/one-click-deploy/hook';
import { RouteParams } from '@/router/type';
import { toastSomethingError } from '@/components/ui/toast-content';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import { useGetDedicateInstanceById } from '../hook/useGetDedicateInstanceById';
import PATH_PAGE from '@/router/path';
import { BASE_URL_BACKEND } from '@/constants';
import { fNameProviderShort, fNameRegion } from '@/utils/formatLabel';

const OneClickDeployDetailOverview = () => {
  const { workspaceId } = useParamWorkspaceId();
  const { instanceId } = useParams<RouteParams>();
  const { data } = useGetDedicateInstanceById();
  const result = {
    instancesName: data?.instance_name,
    instancesStatus: data?.status as StatusGetInstances,
    repository: `https://huggingface.co/${data?.model_repo}`,
    modelName: data?.model_repo ?? '',
    createAt: dayjs(data?.created_at).format('DD/MM/YYYY, HH:mm'),
    instancesGPUDisplay: `${data?.gpu_name} (${data?.vram}GB)`,
    endpoint:
      new URL(`dedicate/${data?.endpoint}/v1/`, BASE_URL_BACKEND).toString() ??
      '',
    apikey: data?.api_key ?? '',
    provider: data?.provider,
    region: data?.region,
    batchSize: data?.batch_size,
    maxInputLen: data?.max_input_len,
    numToken: data?.num_token,
  };

  const regenerateAPI = useRegenerateAPI({
    onError: (error) => {
      console.error(error);
      toastSomethingError();
    },
  });

  const isReady = [StatusGetInstances.Running].includes(
    result?.instancesStatus
  );

  const handleRegenerateAPIKey = () => {
    regenerateAPI.mutate({ instance_id: instanceId! });
  };

  return (
    <>
      <div className="">
        <div className="grid gap-4">
          <div className="flex justify-between">
            <div className="grid w-fit items-center gap-1.5">
              <Label className="flex gap-1 text-muted-foreground">
                <Box className="h-4 w-4" />
                Model
              </Label>
              <a
                className="flex cursor-pointer items-center gap-1 text-primary hover:underline"
                target="_blank"
                rel="noopener noreferrer"
                href={result?.repository}
              >
                {result?.modelName} <ExternalLinkIcon />
              </a>
            </div>
            <div className="flex gap-1 text-nowrap text-xs">
              <ClockIcon />
              Created {result?.createAt}
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="grid w-[100px] items-center gap-1.5">
              <Label className="text-muted-foreground text-xs">
                Batch size
              </Label>
              {fNumber(result?.batchSize || '')}
            </div>
            <div className="grid w-[120px] items-center gap-1.5">
              <Label className="text-muted-foreground text-xs">
                Max input length
              </Label>
              {fNumber(result?.maxInputLen || '')}
            </div>
            <div className="grid w-[100px] items-center gap-1.5">
              <Label className="text-muted-foreground text-xs">
                Number token
              </Label>
              {fNumber(result?.numToken || '')}
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="grid items-center gap-1.5">
              <Label className="flex gap-1 text-muted-foreground">
                <Cpu className="h-4 w-4" />
                Instance
              </Label>
              <div className="flex space-x-2">
                <span>{fNameProviderShort(result?.provider)}</span>
                <Separator orientation="vertical" className="h-5" />
                <span>{fNameRegion(result?.region)}</span>
                <Separator orientation="vertical" className="h-5" />
                <span>{result?.instancesGPUDisplay}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Separator className="my-8" />

      {[StatusGetInstances.Initial, StatusGetInstances.Allocate].includes(
        result?.instancesStatus
      ) && (
        <>
          <Alert className="mb-8">
            <Terminal className="h-4 w-4" />
            <AlertTitle>Deploying ...</AlertTitle>
            <AlertDescription>
              Please wait a moment instances it successfully deployed.
            </AlertDescription>
          </Alert>
        </>
      )}
      {result?.instancesStatus === StatusGetInstances.Failed && (
        <>
          <Alert variant="destructive">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertTitle>Failed</AlertTitle>
            <AlertDescription>
              Please go to the{' '}
              <Link
                to={PATH_PAGE.dashboard.oneClickDeploy.byId.setting(
                  workspaceId,
                  instanceId!
                )}
                className="underline"
              >
                terminated
              </Link>{' '}
              and new create instance.
            </AlertDescription>
          </Alert>
        </>
      )}
      {result?.instancesStatus === StatusGetInstances.Terminated && (
        <>
          <Alert variant="default">
            <ServerOffIcon className="h-4 w-4" />
            <AlertTitle>Terminated</AlertTitle>
            <AlertDescription>
              This instance has been terminated.
            </AlertDescription>
          </Alert>
        </>
      )}
      {[StatusGetInstances.Running].includes(result?.instancesStatus) && (
        <div className={cn('relative flex flex-col gap-4')}>
          <div className="flex items-center gap-1.5">
            <Label className="min-w-28 text-muted-foreground">Endpoint</Label>
            <div className="w-full max-w-md">
              <Input
                value={
                  isReady
                    ? result?.endpoint
                    : 'Endpoint URL is not yet available.'
                }
                readOnly
                endIcon={isReady && <CopyIconButton text={result?.endpoint} />}
                disabled={!isReady}
              />
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="min-w-28 text-muted-foreground">API Key</Label>
            <div className="w-full max-w-md">
              <Input
                value={
                  isReady ? result?.apikey : 'API Key is not yet available.'
                }
                readOnly
                endIcon={isReady && <CopyIconButton text={result?.apikey} />}
                disabled={!isReady}
              />
            </div>
            {isReady && (
              <ConfirmationDialog
                title="Regenerate API Key"
                message="Are you sure you want to re-generate?"
                onConfirm={handleRegenerateAPIKey}
                confirmText="Regenerate new key"
              >
                <Button
                  variant={'secondary'}
                  disabled={regenerateAPI.isPending}
                >
                  Re-generate
                </Button>
              </ConfirmationDialog>
            )}
          </div>
          {isReady && (
            <div className="mt-8">
              <PlaygroundTextGeneration
                title={result?.modelName}
                model={result?.modelName}
                endpoint={result?.endpoint}
                apikey={result?.apikey}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default OneClickDeployDetailOverview;
